import type { StageDef } from "@reagent/sim";
import { freshCardInstances } from "@reagent/sim";
import { NEUTRALISATION } from "../recipes";
import { pollutionCardsFor } from "../pollution";

/**
 * Stage 00 — the induction.
 *
 * This is not one of the five batches a shift is made of; it sits outside the
 * run entirely (see TUTORIAL_STAGE in the package index). A roguelike whose
 * first lesson costs you a shift is a roguelike nobody finishes, so the place
 * to learn which mouse gestures exist is somewhere losing is free.
 *
 * It teaches exactly three verbs and nothing else:
 *
 *  1. **Drag a card onto a machine.** A Cooling Jacket on the mixer.
 *  2. **Connect two machines.** The mixer-to-hopper run is deliberately
 *     missing, because laying a pipe is a *two-click* action and it is the
 *     one interaction in the game nobody guesses. A rig that visibly stops
 *     halfway to the hopper asks the question for us.
 *  3. **Throw the switch.** Everything before the Run phase is planning; the
 *     lever is where a turn is actually spent.
 *
 * The cooling jacket is a *demonstration* here, not a gate. criticalTemp is
 * 20, and clearing the quota costs about 18 heat, so a player who never plays
 * the jacket still clears — while a player who does play it watches the heat
 * readout stop climbing, because a jacket sheds exactly the 1/tick an
 * exotherm adds. Learn the gesture and see what it buys; stage 01 is where the
 * same card becomes the difference between a batch and a crater. Teaching a
 * mitigation and only then requiring it is the order that makes `03-hazards.md` §2.1 land.
 *
 * What this stage deliberately does NOT do is suspend the rules. Leave the rig
 * running with nowhere to put its product and the mixer will cook, exactly as
 * it would anywhere else — it just takes until turn three rather than turn
 * one, which is slow enough that a guided player never sees it and quick
 * enough that an experimenting one learns something true. A tutorial that
 * turns off the game's central claim teaches a game that does not exist.
 */
export const STAGE_00: StageDef = {
  id: "stage-00-induction",
  name: "Induction",
  blurb: "Your first bench. Three things to learn, and plenty of time to learn them.",
  cols: 8,
  rows: 5,
  nodes: [
    { id: "in-a", kind: "inputTank", pos: { x: 0, y: 1 }, emits: { HCl: 1 } },
    { id: "in-b", kind: "inputTank", pos: { x: 0, y: 3 }, emits: { NaOH: 1 } },
    { id: "mix", kind: "reactor", pos: { x: 3, y: 2 }, criticalTemp: 20 },
    { id: "out", kind: "productHopper", pos: { x: 6, y: 2 }, bufferCapacity: 99 },
  ],
  edges: [
    // Both feeds are alloy: corrosion (§2.3) has a stage of its own, and an
    // induction that starves halfway through teaches only frustration.
    {
      id: "e-a",
      fromNodeId: "in-a",
      toNodeId: "mix",
      material: "hastelloy",
      path: [
        { x: 0, y: 1 },
        { x: 1, y: 1 },
        { x: 2, y: 1 },
        { x: 3, y: 1 },
        { x: 3, y: 2 },
      ],
    },
    {
      id: "e-b",
      fromNodeId: "in-b",
      toNodeId: "mix",
      material: "hastelloy",
      path: [
        { x: 0, y: 3 },
        { x: 1, y: 3 },
        { x: 2, y: 3 },
        { x: 3, y: 3 },
        { x: 3, y: 2 },
      ],
    },
    // No mixer-to-hopper edge. That gap is the lesson.
  ],
  recipes: [NEUTRALISATION],
  objective: {
    targetComposition: { NaCl: 3 },
    minPurity: 50,
  },
  energyBudgetKw: 6,
  handSize: 5,
  ticksPerTurn: 8,
  // Generous on purpose: a player reading the panels for the first time should
  // never be losing turns to reading speed.
  maxTurns: 8,
  corrosiveSpecies: ["HCl", "NaOH"],
  // Nothing but the two cards the induction asks for, so any hand can do it.
  // Working out which card to hold is a stage-01 problem.
  startingDeck: freshCardInstances([
    "card-pipe-glass",
    "card-cooling-jacket",
    "card-pipe-glass",
    "card-cooling-jacket",
    "card-pipe-glass",
    "card-cooling-jacket",
    "card-pipe-glass",
    "card-cooling-jacket",
  ]),
  pollutionCards: pollutionCardsFor("overheat", "sludge", "leak", "catalystDead", "burst", "spill", "vermin"),
};
